import API from "$vueCommon/classes/API.js";
import bus from "vue3-eventbus";
import {reactive, toRefs, ref} from 'vue';

const managerBaseUrl = '/manager';
export default class CheckoutAPI extends API {

    static loading = false;
    static rowLoading = {};
    static baseUrl = '';

    static setManagerBaseUrl() {
        console.log('setManagerBaseUrl');
        this.baseUrl = managerBaseUrl;
    }

    static settings() {
        return this.get(this.baseUrl + '/api/checkout/settings');
    }

    static get(url, rowId = null, refreshCart = true) {
        this.loading = true;
        if (rowId) {
            this.rowLoading[rowId] = true;
        }

        return new Promise((resolve, reject) => {
            super.get(url).then(
                response => {
                    this.loading = false;
                    if (rowId) {
                        this.rowLoading[rowId] = false;
                    }

                    resolve(response.data);

                    if (refreshCart) {
                        bus.emit('refreshCart', { data: response.data, rowId });
                    }
                },
                errorResponse => {
                    this.loading = false;
                    if (rowId) {
                        this.rowLoading[rowId] = false;
                    }

                    reject(errorResponse);
                }
            );
        });
    }

    static content() {
        return this.get(this.baseUrl + '/api/cart/content').then(response => response.data);
    }

    static post(url, data = null, rowId = null) {
        this.loading = true;
        if (rowId) {
            this.rowLoading[rowId] = true;
        }

        return new Promise((resolve, reject) => {
            super.post(url, data).then(
                response => {
                    this.loading = false;
                    if (rowId) {
                        this.rowLoading[rowId] = false;
                    }

                    resolve(response.data);
                },
                errorResponse => {
                    this.loading = false;
                    if (rowId) {
                        this.rowLoading[rowId] = false;
                    }

                    reject(errorResponse);
                }
            );
        });
    }

    static postSaveUserIdentity(userIdentity) {
        console.log('CheckoutAPI postSaveUserIdentity');

        const postData = { ...userIdentity };

        const regex = /\+([0-9]{1,4})\.([0-9]{6,20})/gm;
        const phoneParts = regex.exec(postData.phone || '');
        if (phoneParts) {
            postData.phone_prefix = phoneParts[1];
            postData.phone = phoneParts[2];
        }

        const isMainUserIdentity =
            postData.hasOwnProperty('id') &&
            postData.id === null;

        let url;
        if (isMainUserIdentity) {
            url = this.baseUrl + '/api/checkout/save-user';
        } else {
            url = this.baseUrl + '/api/checkout/save-user-identity' + (postData.id ? '/' + postData.id : '');
        }

        return this.post(url, postData, 'user-identity').then(result => {
            if (isMainUserIdentity) {
                result.id = null;
            }

            return result;
        });
    }

    static postDomainRegistrantUserIdentity(rowId, userIdentity, omitValidation) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/domain-registrant-user-identity/' + rowId, {user_identity_id: userIdentity.id})
                .then(response => {
                    let data = {data: response, rowId: rowId};
                    if (!omitValidation) {
                        data = {...data, verifyDomainRequirements: {rowId: rowId, type: 'identity'}};
                    }
                    bus.emit('refreshCart', data);
                    resolve(response);
                })
        });
    }

    static postUserDomainDnsProfile(rowId, dnsProfile) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/domain-user-dns-profile/' + rowId, {user_dns_profile_id: dnsProfile.id})
                .then(response => {
                    bus.emit('refreshCart', {data: response, rowId: rowId});
                    resolve(response);
                })
        });
    }

    static postBillingUserIdentity(userIdentity) {
        return this.post(this.baseUrl + '/api/checkout/billing-user-identity', { user_identity_id: userIdentity.id }, 'user-identity').then(response => {
            if (response.cart_content && response.cart_summary) {
                bus.emit('refreshCart', { data: response });
            }

            return response;
        });
    }

    static postSaveUserDnsProfile(dnsProfile) {
        return this.post(this.baseUrl + '/api/checkout/save-user-dns-profile' + (dnsProfile.id ? '/' + dnsProfile.id : ''), dnsProfile, 'dns-profile');
    }

    static postPaymentMethod(paymentMethodId) {
        return this.post(this.baseUrl + '/api/checkout/payment-method', {payment_method: paymentMethodId}, 'payment-methods');
    }

    static postDiscountCode(discountCode) {
        let data = {discount_code: discountCode};
        if (this.baseUrl === managerBaseUrl) {
            data.cart_instance_name = 'manager';
        }
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/discount-code', data).then(response => {
                resolve(response);
                bus.emit('refreshCart', {data: response});
            }, errorResponse => {
                reject(errorResponse);
            });
        });
    }

    static period(rowId, period) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/period/' + rowId, {period: period}, rowId).then(response => {
                resolve(response);
                bus.emit('refreshCart', {data: response, rowId: rowId});
            });
        });
    }

    static remove(rowId) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/remove/' + rowId, null, rowId).then(response => {
                resolve(response);
                bus.emit('refreshCart', {data: response, rowId: rowId});
            });
        });

    }

    static idProtect(rowId, status = false) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/id-protect/' + rowId, {status: status === true ? 'true' : 'false'}, rowId).then(response => {
                resolve(response);
                bus.emit('refreshCart', {data: response, rowId: rowId});
            });
        });
    }

    static setTaxRate(countryId, companyName = null, taxId = null) {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/set-tax-rate', {country_id: countryId, company_name: companyName, tax_id: taxId}).then(response => {
                resolve(response);
                bus.emit('refreshCart', {data: response});
            });
        });
    }

    static placeOrder() {
        return new Promise((resolve, reject) => {
            this.post(this.baseUrl + '/api/checkout/place-order').then(responseData => {
                console.log('resp', responseData);
                resolve(responseData);
            }, errorResponse => {
                reject(errorResponse);
            });
        });
    }

    static isLoading(rowId = null) {
        return rowId && this.rowLoading && this.rowLoading[rowId] || this.loading;
    }
}
